import React, { useState } from "react";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";

/**
 * 20-20 Dragon Tiger bet table (dt20 / pdt20 / dt202) in the reference admin's markup —
 * rendered inside the page's `.casino-detail`:
 *   .text-right.w-100.pr (main range) + .dtobx-top (Dragon/Tie/Tiger/Pair)
 *   + .teen1daycasino-container (Dragon | Tiger: Even/Odd/Black/Red + 13 cards)
 *   + .casino-remark (only when the feed has a non-empty remark)
 * Styled by the ported reference rules in styles/index.css.
 *
 * Markets are read like the reference (`main` = sub sorted by sr):
 *   0 Dragon, 1 Tiger, 2 Tie, 3 Pair, 4-7 Dragon Even/Odd/Red/Black, 8-20 Dragon cards A..K,
 *   21-24 Tiger Even/Odd/Red/Black, 25-37 Tiger cards A..K — with a lookup by `nat` as fallback.
 */
const IMG = `${import.meta.env.BASE_URL}img/cards`;
const SUIT_IMG = `${import.meta.env.BASE_URL}img/cardIcons`;
const REMARK_ICON = `${import.meta.env.BASE_URL}assets/img/icons/remark.png`;
const CARD_VALUES = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];

// Reference v-kvalmin / v-kvalmax: 1000 → "1K", 100000 → "1L" (2 decimals, trailing zeros dropped)
function formatRangeVal(val) {
  const n = Number(val);
  if (val == null || val === "" || Number.isNaN(n)) return "";
  if (n >= 100000) return `${parseFloat((n / 100000).toFixed(2))}L`;
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(2))}K`;
  return String(parseFloat(n.toFixed(2)));
}

function RangeIcon({ id, label, bet, open, onToggle }) {
  return (
    <div className="float-right">
      <i
        data-toggle="collapse"
        data-target={`#${id}`}
        className="fas fa-info-circle"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
      />{" "}
      <Collapse id={id} open={!!open} className="icon-range">
        {label}:<span>{formatRangeVal(bet?.min)}</span>-<span>{formatRangeVal(bet?.max)}</span>
      </Collapse>
    </div>
  );
}

export default function BetTableDragon({ data = [], onBetClick, exposures = {}, remark }) {
  // Range popups (#demo1..#demo5), toggled like the reference's bootstrap collapse
  const [openRange, setOpenRange] = useState({});
  const toggleRange = (id) => setOpenRange((s) => ({ ...s, [id]: !s[id] }));

  const main = [...(Array.isArray(data) ? data : [])].sort((a, b) => Number(a?.sr) - Number(b?.sr));
  const byName = (name) => data.find((item) => item?.nat === name);
  // Market at reference index i, falling back to its `nat` name
  const bet = (i, name) => {
    const m = main[i];
    const named = byName(name);
    if (named) return named;
    return m || { nat: name };
  };
  const cardBet = (side, idx) => {
    const v = CARD_VALUES[idx];
    const base = side === "Dragon" ? 8 : 25;
    return (
      byName(`${side} Card ${v}`) ||
      (v === "A" ? byName(`${side} Card 1`) : null) ||
      main[base + idx] || { nat: `${side} Card ${v}` }
    );
  };

  const isSuspended = (b) => String(b?.gstatus || "").toUpperCase() !== "OPEN";
  const odds = (b) => (b?.b !== undefined && b?.b !== null && b?.b !== "" ? b.b : 0);
  const getExposure = (name) => {
    const v = exposures[name] ?? exposures[name?.toLowerCase?.()];
    return v != null ? Number(v) || 0 : 0;
  };
  // Reference v-casinofancybook: amount > 0 → book-green, otherwise book-red
  const bookClass = (exp) => (exp > 0 ? "book-green" : "book-red");
  const click = (b) => {
    if (isSuspended(b)) return;
    onBetClick?.(b?.nat, odds(b), b);
  };

  const topBox = (cls, label, b) => {
    const exp = getExposure(b?.nat ?? label);
    return (
      <div className={`${cls}${isSuspended(b) ? " suspended" : ""}`} onClick={() => click(b)}>
        <div>
          <b>{label}</b>
        </div>{" "}
        <div className="text-center">
          <span className="d-block">
            <b>{odds(b)}</b>
          </span>{" "}
          <span className={`d-block casino-book ${bookClass(exp)}`}>{formatBook(exp)}</span>
        </div>
      </div>
    );
  };

  const blBox = (b, content, extraClass = "") => {
    const exp = getExposure(b?.nat);
    return (
      <div className={`casino-bl-box${isSuspended(b) ? " suspended" : ""}`}>
        <div className={`back casino-bl-box-item${extraClass}`} onClick={() => click(b)}>
          {content}{" "}
          <span className={`casino-book ${bookClass(exp)}`}>{formatBook(exp)}</span>
        </div>
      </div>
    );
  };

  const side = (name, off, cardsRangeId, rangeId) => {
    const even = bet(off, `${name} Even`);
    const odd = bet(off + 1, `${name} Odd`);
    const red = bet(off + 2, `${name} Red`);
    const black = bet(off + 3, `${name} Black`);
    const firstCard = cardBet(name, 0);
    return (
      <>
        <div>
          <div className="casino-box-row justify-content-center casino-odds casino-title">
            <div className="text-left w-100">
              <b className={name === "Dragon" ? "text-playera" : "text-playerb"}>{name}</b>{" "}
              <RangeIcon
                id={rangeId}
                label="R"
                bet={even}
                open={!!openRange[rangeId]}
                onToggle={() => toggleRange(rangeId)}
              />
            </div>
          </div>{" "}
          <div className="casino-box-row">
            <div className="casino-bl-box">
              <b>{odds(even)}</b>
            </div>{" "}
            <div className="casino-bl-box">
              <b>{odds(odd)}</b>
            </div>{" "}
            <div className="casino-bl-box">
              <b>{odds(red)}</b>
            </div>{" "}
            <div className="casino-bl-box">
              <b>{odds(black)}</b>
            </div>
          </div>{" "}
          <div className="casino-box-row">
            {blBox(even, <span className="casino-box-odd">Even</span>)}{" "}
            {blBox(odd, <span className="casino-box-odd">Odd</span>)}{" "}
            {blBox(
              black,
              <span>
                <img src={`${SUIT_IMG}/spade.png`} alt="" /> <img src={`${SUIT_IMG}/club.png`} alt="" />
              </span>,
              " casino-card-img"
            )}{" "}
            {blBox(
              red,
              <span>
                <img src={`${SUIT_IMG}/heart.png`} alt="" /> <img src={`${SUIT_IMG}/diamond.png`} alt="" />
              </span>,
              " casino-card-img"
            )}
          </div>
        </div>{" "}
        <div className="casino-box cards-box">
          <div className="w-100">
            <div className="casino-odds casino-cards-odds-title">
              <div className="text-center w-100">
                <b>{odds(firstCard)}</b>{" "}
                <RangeIcon
                  id={cardsRangeId}
                  label="R"
                  bet={firstCard}
                  open={!!openRange[cardsRangeId]}
                  onToggle={() => toggleRange(cardsRangeId)}
                />
              </div>
            </div>{" "}
            <div className="casino-cards text-center mt-1">
              {CARD_VALUES.map((v, idx) => {
                const b = cardBet(name, idx);
                const exp = getExposure(b?.nat);
                return (
                  <React.Fragment key={v}>
                    {idx > 0 && " "}
                    <div className="casino-card-item" onClick={() => click(b)}>
                      <div className={`card-image${isSuspended(b) ? " suspended" : ""}`}>
                        <img src={`${IMG}/${v}.png`} alt="" />
                      </div>{" "}
                      <div className={`casino-book ${bookClass(exp)}`}>{formatBook(exp)}</div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  };

  const dragon = bet(0, "Dragon");
  const tiger = bet(1, "Tiger");
  const tie = bet(2, "Tie");
  const pair = bet(3, "Pair");

  return (
    <>
      <div className="text-right w-100 pr">
        <RangeIcon
          id="demo1"
          label="Range"
          bet={dragon}
          open={!!openRange.demo1}
          onToggle={() => toggleRange("demo1")}
        />
      </div>{" "}
      <div className="dtobx-top">
        {topBox("dragon-box", "Dragon", dragon)}{" "}
        {topBox("tiebox", "Tie", tie)}{" "}
        {topBox("tiger-box", "Tiger", tiger)}{" "}
        {topBox("pair-box", "Pair", pair)}
      </div>{" "}
      <div className="teen1daycasino-container mt-5">
        <div className="teen1dayleft">{side("Dragon", 4, "demo3", "demo2")}</div>{" "}
        <div className="teen1daycenter" />{" "}
        <div className="teen1dayright">{side("Tiger", 21, "demo5", "demo4")}</div>
      </div>{" "}
      {remark != null && remark !== "" && (
        <div className="casino-remark mt-3">
          <div className="remark-icon">
            <img src={REMARK_ICON} alt="" />
          </div>{" "}
          {React.createElement("marquee", null, remark)}
        </div>
      )}
    </>
  );
}
